// Empire Apps Chassis · connectors/govt · shared contract
// Every govt connector implements ConnectorAdapter<TStatus, TPayload>.
// Test mode is the default. Production OAuth flow lives behind DEV flag and
// the integrating app must own the redirect URI + secret rotation.

export type ConnectorMode = 'test' | 'live';

export interface ConnectorStatus {
  readonly id: string;
  readonly mode: ConnectorMode;
  readonly connected: boolean;
  readonly lastSyncAt: string | null;
  readonly principalIdHash: string | null;
  readonly notes: string;
}

export interface ConnectorError {
  readonly code:
    | 'not-configured'
    | 'auth-cancelled'
    | 'consent-missing'
    | 'network'
    | 'parse'
    | 'signature'
    | 'rate-limited'
    | 'unsupported'
    | 'live-disabled';
  readonly message: string;
  readonly retriable: boolean;
}

export type ConnectorResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ConnectorError };

export interface ConnectorAdapter<TStatus extends ConnectorStatus, TPayload> {
  readonly id: string;
  readonly displayName: string;
  readonly displayNameHi: string;
  readonly dpdpCategory: string; // human-readable, surfaced in consent prompts
  readonly mode: ConnectorMode;
  getStatus(): Promise<TStatus>;
  connect(): Promise<ConnectorResult<TStatus>>;
  disconnect(): Promise<void>;
  importPayload(): Promise<ConnectorResult<TPayload>>;
  mockPayload(): TPayload;
}

export const ok = <T>(value: T): ConnectorResult<T> => ({ ok: true, value });
export const err = <T>(error: ConnectorError): ConnectorResult<T> => ({ ok: false, error });

export function notConfigured(message: string): ConnectorError {
  return { code: 'not-configured', message, retriable: false };
}

export function liveDisabled(connectorId: string): ConnectorError {
  return {
    code: 'live-disabled',
    message: `${connectorId} live mode is gated behind DEV flag and requires production credentials.`,
    retriable: false,
  };
}

export function isOk<T>(r: ConnectorResult<T>): r is { ok: true; value: T } {
  return r.ok;
}

export function nowIso(): string {
  return new Date().toISOString();
}

// SHA-256 over UTF-8 bytes, base64.
export async function shortHash(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  let s = '';
  const bytes = new Uint8Array(buf);
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).slice(0, 16);
}

// Returns the configured DEV flag from the integrating app. Apps set
// globalThis.__EMPIRE_CONNECTORS_DEV__ = true to opt into live flows.
export function devModeEnabled(): boolean {
  const g = globalThis as unknown as { __EMPIRE_CONNECTORS_DEV__?: boolean };
  return g.__EMPIRE_CONNECTORS_DEV__ === true;
}

// Standardised retry-aware fetch wrapper. Govt APIs are slow and flaky.
export async function fetchWithBudget(
  url: string,
  init: RequestInit,
  opts: { readonly retries: number; readonly timeoutMs: number }
): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= opts.retries; attempt++) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), opts.timeoutMs);
    try {
      const res = await fetch(url, { ...init, signal: ctl.signal });
      clearTimeout(timer);
      if (res.status >= 500 && attempt < opts.retries) {
        await sleep(250 * (attempt + 1));
        continue;
      }
      return res;
    } catch (e) {
      clearTimeout(timer);
      lastErr = e;
      if (attempt >= opts.retries) break;
      await sleep(250 * (attempt + 1));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('fetch-failed');
}

export function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}
